import { ArrowLeft, ArrowRight, Calculator, Eraser } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import { Alert, AlertDescription } from "src/components/ui/alert";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader } from "src/components/ui/card";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";
import { AmortizationSection } from "./components/AmortizationSection";
import { FinancingStepForm } from "./components/FinancingStepForm";
import { RentalStepForm } from "./components/RentalStepForm";
import { RentVsBuyResponse } from "./components/RentVsBuyResponse";
import type {
  AmortizationPlan,
  ComparisonResult,
  CorrectionPlan,
} from "./types";
import { calculateRentVsBuy, parseBrlToNumber } from "./utils/functions";

const calculatorKey = "alugar-vs-financiar";

export const AlugarVsFinanciar: React.FC = () => {
  const [step, setStep] = useState<number>(1);
  const [valorImovel, setValorImovel] = useState<string>("");
  const [valorAluguel, setValorAluguel] = useState<string>("");
  const [valorizacao, setValorizacao] = useState<string>("");
  const [igpm, setIgpm] = useState<string>("");
  const [entrada, setEntrada] = useState<string>("");
  const [custos, setCustos] = useState<string>("");
  const [prazo, setPrazo] = useState<string>("");
  const [taxaAnual, setTaxaAnual] = useState<string>("");
  const [rentabilidade, setRentabilidade] = useState<string>("");
  const [sistemaAmortizacao, setSistemaAmortizacao] = useState<string>("0");
  const [dataInicioFinanciamento, setDataInicioFinanciamento] =
    useState<string>(new Date().toISOString().split("T")[0]);

  const [amortizationPlans, setAmortizationPlans] = useState<
    AmortizationPlan[]
  >([]);
  const [correctionPlans, setCorrectionPlans] = useState<CorrectionPlan[]>([]);

  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [results, setResults] = useState<ComparisonResult | null>(null);
  const isVerified = useIsVerified();

  const handleCalculate = async (overrides?: {
    amortizationPlans?: AmortizationPlan[];
    correctionPlans?: CorrectionPlan[];
  }) => {
    setError("");

    if (
      !valorImovel ||
      !valorAluguel ||
      !valorizacao ||
      !igpm ||
      !entrada ||
      !custos ||
      !prazo ||
      !taxaAnual ||
      !rentabilidade ||
      !sistemaAmortizacao
    ) {
      setError("Volte e confira se preencheu todos os campos.");
      return;
    }

    const params = {
      valorImovel: parseBrlToNumber(valorImovel),
      valorAluguel: parseBrlToNumber(valorAluguel),
      valorizacao: Number.parseFloat(valorizacao),
      igpm: Number.parseFloat(igpm),
      entrada: parseBrlToNumber(entrada),
      custos: parseBrlToNumber(custos),
      prazo: Number.parseInt(prazo),
      taxaAnual: Number.parseFloat(taxaAnual),
      rentabilidade: Number.parseFloat(rentabilidade),
      amortizationSystemType: Number.parseInt(sistemaAmortizacao),
      dataInicioFinanciamento,
      amortizationPlans: overrides?.amortizationPlans ?? amortizationPlans,
      correctionPlans: overrides?.correctionPlans ?? correctionPlans,
    };

    setIsLoading(true);
    try {
      const calculatedResults = await calculateRentVsBuy(params);
      setResults(calculatedResults);
    } catch (err: any) {
      setError(err.message || "Erro ao calcular");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setValorImovel("");
    setValorAluguel("");
    setValorizacao("");
    setIgpm("");
    setEntrada("");
    setCustos("");
    setPrazo("");
    setTaxaAnual("");
    setRentabilidade("");
    setSistemaAmortizacao("0");
    setDataInicioFinanciamento(new Date().toISOString().split("T")[0]);
    setAmortizationPlans([]);
    setCorrectionPlans([]);
    setResults(null);
    setError("");
    setStep(1);
  };

  const handleAmortizationPlansChange = (plans: AmortizationPlan[]) => {
    setAmortizationPlans(plans);
    if (results !== null) handleCalculate({ amortizationPlans: plans });
  };

  const handleCorrectionPlansChange = (plans: CorrectionPlan[]) => {
    setCorrectionPlans(plans);
    if (results !== null) handleCalculate({ correctionPlans: plans });
  };

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      valorImovel,
      valorAluguel,
      valorizacao,
      igpm,
      entrada,
      custos,
      prazo,
      taxaAnual,
      rentabilidade,
      sistemaAmortizacao,
      dataInicioFinanciamento,
      amortizationPlans,
      correctionPlans,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setValorImovel((savedData.valorImovel as string) || "");
    setValorAluguel((savedData.valorAluguel as string) || "");
    setValorizacao((savedData.valorizacao as string) || "");
    setIgpm((savedData.igpm as string) || "");
    setEntrada((savedData.entrada as string) || "");
    setCustos((savedData.custos as string) || "");
    setPrazo((savedData.prazo as string) || "");
    setTaxaAnual((savedData.taxaAnual as string) || "");
    setRentabilidade((savedData.rentabilidade as string) || "");
    setSistemaAmortizacao((savedData.sistemaAmortizacao as string) || "0");
    setDataInicioFinanciamento(
      (savedData.dataInicioFinanciamento as string) ||
        new Date().toISOString().split("T")[0],
    );
    if (savedData.amortizationPlans)
      setAmortizationPlans(savedData.amortizationPlans as AmortizationPlan[]);
    if (savedData.correctionPlans)
      setCorrectionPlans(savedData.correctionPlans as CorrectionPlan[]);
  }, []);

  return (
    <div className="w-full  mx-auto">
      <Card hideBorder className="w-full">
        <CardHeader className="p-0 max-md:mb-4 md:mb-10 md:p-6">
          <h1 className="text-primary text-2xl font-bold">
            Alugar ou financiar um imóvel?
          </h1>
        </CardHeader>
        <CardContent className="p-1 md:p-6 border-none">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (step === 1) {
                setStep(2);
              }
            }}
            className="grid gap-4"
          >
            {/* Progress indicators */}
            <div className="mb-6">
              <div className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      step >= 1
                        ? "bg-primary text-white shadow-md shadow-primary/30"
                        : "border-2 border-gray-300 text-gray-400"
                    }`}
                  >
                    1
                  </div>
                  <span
                    className={`text-xs mt-1.5 font-medium whitespace-nowrap transition-colors ${
                      step === 1 ? "text-primary" : "text-gray-400"
                    }`}
                  >
                    Financiamento
                  </span>
                </div>
                <div
                  className={`h-0.5 flex-1 mx-3 mb-5 transition-colors duration-300 ${
                    step >= 2 ? "bg-primary" : "bg-gray-200"
                  }`}
                />
                <div className="flex flex-col items-center">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      step >= 2
                        ? "bg-primary text-white shadow-md shadow-primary/30"
                        : "border-2 border-gray-300 text-gray-400"
                    }`}
                  >
                    2
                  </div>
                  <span
                    className={`text-xs mt-1.5 font-medium whitespace-nowrap transition-colors ${
                      step === 2 ? "text-primary" : "text-gray-400"
                    }`}
                  >
                    Aluguel & Investimentos
                  </span>
                </div>
              </div>
            </div>

            {step === 1 && (
              <FinancingStepForm
                valorImovel={valorImovel}
                setValorImovel={setValorImovel}
                entrada={entrada}
                setEntrada={setEntrada}
                custos={custos}
                setCustos={setCustos}
                dataInicioFinanciamento={dataInicioFinanciamento}
                setDataInicioFinanciamento={setDataInicioFinanciamento}
                prazo={prazo}
                setPrazo={setPrazo}
                taxaAnual={taxaAnual}
                setTaxaAnual={setTaxaAnual}
                sistemaAmortizacao={sistemaAmortizacao}
                setSistemaAmortizacao={setSistemaAmortizacao}
              />
            )}

            {step === 2 && (
              <RentalStepForm
                valorAluguel={valorAluguel}
                setValorAluguel={setValorAluguel}
                valorizacao={valorizacao}
                setValorizacao={setValorizacao}
                igpm={igpm}
                setIgpm={setIgpm}
                rentabilidade={rentabilidade}
                setRentabilidade={setRentabilidade}
              />
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-4 mt-6">
              <Button
                onClick={handleClear}
                variant="ghost"
                type="button"
                className="text-gray-500 w-full sm:w-auto"
              >
                <Eraser className="w-4 h-4 mr-2" />
                Limpar
              </Button>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                {step === 2 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="w-full sm:w-auto"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                )}
                {step === 1 ? (
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full sm:w-auto"
                  >
                    Avançar
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    disabled={!isVerified || isLoading}
                    type="button"
                    variant="default"
                    className="w-full sm:w-[150px]"
                    onClick={() => {
                      handleSave();
                      checkCalculatorUsageLimitAndRedirect(handleCalculate);
                    }}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    {isLoading ? "Calculando..." : "Calcular"}
                  </Button>
                )}
              </div>
            </div>

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </form>

          {results && !isLoading && (
            <>
              <AmortizationSection
                amortizationPlans={amortizationPlans}
                setAmortizationPlans={handleAmortizationPlansChange}
                correctionPlans={correctionPlans}
                setCorrectionPlans={handleCorrectionPlansChange}
                onError={setError}
              />
              <RentVsBuyResponse results={results} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
